// Throttle 1/sec prevents SR spam during live runs; token_delta filtered out
// (delta volume drowns audio output). Errors bypass throttle — announced immediately.
import { useEffect, useRef } from "react";
import type { RunEvent } from "@/types/workflow.types";

const THROTTLE_MS = 1000;

// Event types that produce user-facing state changes worth announcing.
// token_delta intentionally excluded — high-frequency, no discrete SR value.
const SKIP_TYPES = new Set(["token_delta"]);

function normalizedEventType(eventType: string): string {
  return eventType.replace(/\./g, "_");
}

interface UseSSEAnnouncerOptions {
  /** Callback to update the live region text content. */
  setAnnouncement: (text: string) => void;
  /** Map from eventType → localized label string. */
  eventLabels: Record<string, string>;
}

/**
 * Subscribes to the events array and emits at most one announcement per second
 * to the live region. Errors (run_failed / step_failed / cap_exceeded) bypass
 * the throttle so screen-reader users are notified immediately.
 */
export function useSSEAnnouncer(
  events: RunEvent[],
  { setAnnouncement, eventLabels }: UseSSEAnnouncerOptions
): void {
  const lastLengthRef = useRef(0);
  const lastAnnouncedAtRef = useRef(0);
  const pendingRef = useRef<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const newEvents = events.slice(lastLengthRef.current);
    lastLengthRef.current = events.length;

    for (const ev of newEvents) {
      const eventType = normalizedEventType(ev.eventType);
      if (SKIP_TYPES.has(eventType)) continue;

      const label = eventLabels[eventType];
      if (!label) continue;

      const isError =
        eventType.includes("fail") ||
        eventType.includes("cap_exceeded") ||
        eventType.includes("rejected");

      if (isError) {
        // Errors bypass throttle — announce immediately.
        if (timerRef.current) clearTimeout(timerRef.current);
        pendingRef.current = null;
        lastAnnouncedAtRef.current = Date.now();
        setAnnouncement(label);
        return;
      }

      // Non-error: queue for throttled emission.
      pendingRef.current = label;
    }

    if (pendingRef.current === null) return;

    const now = Date.now();
    const msSinceLast = now - lastAnnouncedAtRef.current;

    if (msSinceLast >= THROTTLE_MS) {
      lastAnnouncedAtRef.current = now;
      setAnnouncement(pendingRef.current);
      pendingRef.current = null;
    } else {
      if (!timerRef.current) {
        timerRef.current = setTimeout(() => {
          timerRef.current = null;
          if (pendingRef.current) {
            lastAnnouncedAtRef.current = Date.now();
            setAnnouncement(pendingRef.current);
            pendingRef.current = null;
          }
        }, THROTTLE_MS - msSinceLast);
      }
    }
  }, [events, setAnnouncement, eventLabels]);

  // Cleanup timer on unmount.
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);
}
